// Verified-fact snapshot for the public comparison pages (Interview Query
// alternatives, best AI engineer prep platforms). Every non-Promptbound claim
// traces back to gtm/PROSPECTING-PLAYBOOK.md or gtm/SWIPE-FILE.md, both dated
// 2026-07-10 unless noted otherwise. Where we don't have a verified number for
// something, we say so instead of guessing, per the playbook's own "include
// competitors honestly, no disparagement" rule.

export const PROMPTBOUND = {
  id: "promptbound",
  name: "Promptbound",
  bestFor: "AI/ML engineer prep: free curriculum plus graded mock interviews",
  priceShort: "$20/mo or $192/yr, unlimited",
  priceDetail:
    "All 40 lessons are free forever, no card required. The paid tier ($20/mo or $192/yr) unlocks unlimited graded mock interviews with Altaris; 3 graded interviews are free, also no card.",
  reviewsShort: "New, no independent track record yet",
  reviewsDetail:
    "Promptbound is new enough that it has no Trustpilot, G2 or Product Hunt footprint yet. We're not going to pretend otherwise, that's exactly the kind of gap this page is trying not to paper over for anyone.",
  note: "72 of Altaris's roughly 177 questions are adapted from real 2024-2026 interview loops at Meta, Google, Amazon, OpenAI and Anthropic. Grading uses a hidden rubric you never see beforehand: it's graded practice, not a live-interview copilot.",
};

export const COMPETITORS = [
  {
    id: "interview-kickstart",
    name: "Interview Kickstart",
    bestFor: "Live, instructor-led bootcamp, including an ML-specific track",
    priceShort: "$5,000 to $12,000+ per cohort",
    priceDetail:
      "Cohort pricing runs roughly $5,000 to $12,000+. Individual buyers on Hacker News have quoted $5,500 (2022) and $8,500 for a two-month program (2021). They also run an ML Flagship track aimed specifically at machine learning engineers.",
    reviewsShort: "~4.6/5, ~514 reviews (Trustpilot)",
    reviewsDetail:
      "About 514 Trustpilot reviews averaging roughly 4.6/5 as of a 2026-07-10 search snapshot. Trustpilot's own page reportedly carries a review-solicitation compliance notice, so we're citing the average with that caveat attached rather than leaving it out.",
    note: "The 1-star tail includes serious complaints, including a refund dispute and a claim of a forged contract. Both the high average and that tail are real at the same time.",
  },
  {
    id: "final-round-ai",
    name: "Final Round AI",
    bestFor: "AI-driven mock interview practice",
    priceShort: "$148/mo",
    priceDetail: "$148/mo, per pricing figures cited in community discussion of the category.",
    reviewsShort: "~3.3/5 Trustpilot vs. ~4.9/5 Product Hunt",
    reviewsDetail:
      "About 261 Trustpilot reviews averaging roughly 3.3/5 with 23% 1-star, versus 72 Product Hunt reviews averaging 4.9/5, both as of 2026-07-10 snapshots. That gap between the two platforms is itself worth knowing about before you weight either one heavily.",
    note: "Refund and cancellation complaints, including unwanted auto-renewal charges, show up repeatedly in the Trustpilot reviews.",
  },
  {
    id: "educative",
    name: "Educative",
    bestFor: "General technical course library (not AI-engineer-role-specific)",
    priceShort: "Not verified in our research",
    priceDetail:
      "We don't have a verified figure for current tiers. One 2024 complaint cited an annual auto-renewal charge of about £189.",
    reviewsShort: "~3.7/5 Trustpilot, ~1.8/5 Product Hunt",
    reviewsDetail:
      "About 57 Trustpilot reviews averaging roughly 3.7/5, and 8 Product Hunt reviews averaging 1.8/5, as of 2026-07-10 snapshots.",
    note: "Auto-renewal without warning and refused refunds are the recurring theme across both review sources.",
  },
  {
    id: "exponent",
    name: "Exponent",
    bestFor: "Broad tech and PM interview prep (not AI-engineer-specific)",
    priceShort: "Not verified in our research",
    priceDetail: "We don't have a verified figure for current tiers.",
    reviewsShort: "3.0/5, 2 reviews (Product Hunt)",
    reviewsDetail:
      "2 Product Hunt reviews averaging 3.0/5 as of a 2026-07-10 snapshot, too small a sample to lean on by itself.",
    note: "A 2026 commenter said they'd look elsewhere for AI-specific roles. A 2024 commenter wanted more free sample content before paying, and a 2023 review raised concerns about paid content quality. All three are about general tech/PM prep, not an AI-engineer track.",
  },
  {
    id: "leetcode",
    name: "LeetCode",
    bestFor: "Algorithm and data-structure grinding",
    priceShort: "Not verified in our research",
    priceDetail: "We don't have a verified figure for current subscription pricing.",
    reviewsShort: "~2.5/5 (Trustpilot)",
    reviewsDetail:
      "Roughly 2.5/5 on Trustpilot per a 2026-07-10 search snapshot; the review count wasn't confirmable. Auto-renewal complaints appear here as well.",
    note: "Not an AI-engineer-role-specific mock interview product, it's the default for coding-problem practice. Community sentiment around it skews toward burnout ('blindly grinding LeetCode is a massive waste of time') more than trust or billing complaints.",
  },
];

// Interview Query, interviewing.io, Hello Interview and NeetCode: named as
// competitors in the playbook, but "essentially no review-site footprint"
// turned up in the same research pass. We say that plainly rather than
// inventing pricing or features for them.
export const SPARSE_FOOTPRINT_NOTE =
  "Our 2026-07-10 review-site sweep found essentially no Trustpilot, G2 or Product Hunt footprint for these, so we can't independently verify pricing, satisfaction or content depth the way we can for the platforms above. That's not a knock on any of them, it just means we'd rather point you to their own sites for current details than guess.";
